/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { type RouteObject } from 'react-router-dom'

import Feature from '@modules/utils/feature'
import Redirect from '@modules/routing/Redirect'

import { getSupportedSliderInstanceTypes } from '@/lib/sliders/support'

import { isFeatureActivated } from '../../selectors/config'

import type { ReduxState } from '@/types/redux'

const defaultPostLoginLocation = `/deployments`

export function getRedirects({ state }: { state: ReduxState }): RouteObject[] {
  const postLoginLocation = getPostLoginLocation(state)

  return [
    {
      path: '/',
      element: <Redirect to={postLoginLocation} />,
    },

    ...getPlatformRedirects(),
    ...getPlatformRbacRedirects(),
    ...getLegacyActivityFeedRedirects(),
    ...getStackDeploymentRedirects(),
  ]

  function getPlatformRedirects(): RouteObject[] {
    return [
      {
        path: '/region/:regionId',
        element: <Redirect to={(params) => `/region/${params.regionId}/platform`} />,
      },
      {
        path: '/region/:regionId/templates',
        element: <Redirect to={(params) => `/region/${params.regionId}/templates/deployments`} />,
      },
      {
        path: '/region/:regionId/runners',
        element: <Redirect to={(params) => `/region/${params.regionId}/hosts`} />,
      },
      {
        path: '/region/:regionId/runners/:runnerId',
        element: (
          <Redirect to={(params) => `/region/${params.regionId}/hosts/${params.runnerId}`} />
        ),
      },
      {
        path: '/region/:regionId/runners/:runnerId/roles',
        element: (
          <Redirect to={(params) => `/region/${params.regionId}/hosts/${params.runnerId}`} />
        ),
      },
      {
        path: '/region/:regionId/runners/:runnerId/delete',
        element: (
          <Redirect to={(params) => `/region/${params.regionId}/hosts/${params.runnerId}`} />
        ),
      },
      {
        path: '/region/:regionId/allocators',
        element: <Redirect to={(params) => `/region/${params.regionId}/hosts/allocators`} />,
      },
      {
        path: '/region/:regionId/allocators/:allocatorId',
        element: (
          <Redirect
            to={(params) => `/region/${params.regionId}/hosts/${params.allocatorId}/allocator`}
          />
        ),
      },
      {
        path: '/region/:regionId/allocators/:allocatorId/vacate',
        element: (
          <Redirect
            to={(params) => `/region/${params.regionId}/hosts/${params.allocatorId}/allocator`}
          />
        ),
      },
      {
        path: '/region/:regionId/allocators/:allocatorId/delete',
        element: (
          <Redirect
            to={(params) => `/region/${params.regionId}/hosts/${params.allocatorId}/allocator`}
          />
        ),
      },
      {
        path: '/region/:regionId/proxies',
        element: <Redirect to={(params) => `/region/${params.regionId}/hosts/proxies`} />,
      },
      {
        path: '/region/:regionId/constructors',
        element: <Redirect to={(params) => `/region/${params.regionId}/hosts/control-planes`} />,
      },
    ]
  }

  function getPlatformRbacRedirects(): RouteObject[] {
    const manageRbac = isFeatureActivated(state, Feature.manageRbac)

    if (!manageRbac) {
      return []
    }

    return [
      {
        path: '/region/:regionId/manage',
        element: (
          <Redirect to={(params) => `/region/${params.regionId}/manage/authentication-providers`} />
        ),
      },
    ]
  }

  function getLegacyActivityFeedRedirects(): RouteObject[] {
    return getSupportedSliderInstanceTypes().map((sliderInstanceType) => ({
      path: `/activity-feed/${sliderInstanceType}`,
      element: <Redirect to='/activity-feed' />,
    }))
  }

  function getStackDeploymentRedirects(): RouteObject[] {
    return [
      {
        path: '/region/:regionId/deployment/:clusterId',
        element: (
          <Redirect
            to={(params) => `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}`}
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/edit',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/edit/advanced',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit/advanced`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/elasticsearch`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/config',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/config/advanced',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit/advanced`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/console',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/elasticsearch/console`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/security',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/security`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/snapshots',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/elasticsearch/snapshots`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/elasticsearch/snapshots/:snapshotName',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/elasticsearch/snapshots/${params.snapshotName}`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/kibana',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/kibana`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/kibana/edit',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/kibana/edit/advanced',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit/advanced`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/apm',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/apm`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/apm/edit/advanced',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/edit/advanced`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/security',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/security`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/operations',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/operations`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/operations/heap-dumps',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/operations/heap-dumps`
            }
          />
        ),
      },
      {
        path: '/region/:regionId/deployment/:clusterId/activity',
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/activity`
            }
          />
        ),
      },
      ...getSupportedSliderInstanceTypes().map((sliderInstanceType) => ({
        path: `/region/:regionId/deployment/:clusterId/activity/${sliderInstanceType}`,
        element: (
          <Redirect
            to={(params) =>
              `/deployments/resolve/cluster/${params.regionId}/${params.clusterId}/activity/${sliderInstanceType}`
            }
          />
        ),
      })),
      {
        path: '/deployments/:deploymentId/monitoring',
        element: <Redirect to={(params) => `/deployments/${params.deploymentId}/health`} />,
      },
    ]
  }
}

function getPostLoginLocation(state) {
  const {
    config: { postLoginLocation = defaultPostLoginLocation },
  } = state

  return postLoginLocation
}
